import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus, Bot, Trash2, Save, Phone, MessageSquare, CalendarDays, Users, Loader2,
  Settings, PlayCircle, Inbox, ArrowLeft, Building2, Sparkles, Code2, Copy, Check, Mail,
  BarChart3, Bell, Clock,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import {
  receptionistList, receptionistCreate, receptionistUpdate, receptionistDelete,
  receptionistCalls, receptionistMessages, receptionistSummary, receptionistSummarizeCall,
  telephonyStatus, receptionistDigestTest, receptionistAnalytics, receptionistAppointments, receptionistRemind,
} from "@/lib/api";
import { ReceptionistTestConsole } from "@/components/receptionist/ReceptionistTestConsole";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BLANK = {
  name: "Ava", business_name: "", greeting: "", kb_facts: "", faqs: [],
  hours_note: "", transfer_number: "", phone_number: "", notify_emails: [], voice: "alloy",
  languages: ["English"], active: true,
  hours_enabled: false, timezone: "UTC", open_days: [0, 1, 2, 3, 4], open_time: "09:00", close_time: "17:00", after_hours_message: "",
  slot_minutes: 30, digest_enabled: false,
};
const VOICES = ["alloy", "echo", "fable", "onyx", "nova", "shimmer", "coral", "sage"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const OUTCOME_STYLES = {
  booked: "bg-emerald-50 text-emerald-700", message: "bg-amber-50 text-amber-700",
  lead: "bg-blue-50 text-blue-700", transfer: "bg-violet-50 text-violet-700",
  inquiry: "bg-slate-100 text-slate-600", other: "bg-slate-100 text-slate-500",
};

export default function ReceptionistStudio() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // receptionist object or "new"
  const [tab, setTab] = useState("setup");           // setup | test | calls | messages
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await receptionistList(isAdmin);
      setList(d.receptionists || []);
    } catch { setList([]); }
    finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { if (user) load(); }, [user, load]);

  if (user === undefined) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if (user === null) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 grid place-items-center px-6">
          <div className="max-w-md text-center" data-testid="receptionist-login-gate">
            <Bot className="w-10 h-10 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">AI Receptionist Studio</h1>
            <p className="text-slate-600 mb-6">Sign in to create and manage your 24/7 AI receptionist — it answers questions, takes messages and books appointments for your business.</p>
            <Link to="/login" data-testid="receptionist-login-link" className="inline-block px-6 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">Sign in to continue</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 pt-28 pb-10" data-testid="receptionist-studio">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">AI Receptionist</h1>
            <p className="text-slate-600 mt-1 text-sm">Your always-on virtual receptionist — grounded in your business, ready for voice.</p>
          </div>
          <button
            onClick={() => { setSelected("new"); setTab("setup"); }}
            data-testid="new-receptionist-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" /> New receptionist
          </button>
        </div>

        {!selected && (
          <ListView list={list} loading={loading} onOpen={(r) => { setSelected(r); setTab("setup"); }} onTest={(r) => { setSelected(r); setTab("test"); }} isAdmin={isAdmin} />
        )}

        {selected && (
          <EditorView
            key={selected === "new" ? "new" : selected.id}
            initial={selected === "new" ? BLANK : { ...BLANK, ...selected }}
            isNew={selected === "new"}
            tab={tab} setTab={setTab}
            onBack={() => { setSelected(null); load(); }}
            afterSave={(saved) => { setSelected(saved); load(); }}
            afterDelete={() => { setSelected(null); load(); }}
          />
        )}
      </main>
      <Footer />
    </div>
  );
}

function ListView({ list, loading, onOpen, onTest, isAdmin }) {
  if (loading) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (list.length === 0) return (
    <div className="py-16 text-center rounded-2xl border border-dashed border-slate-300 bg-white" data-testid="receptionist-empty">
      <Bot className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <p className="text-slate-600">No receptionists yet. Create your first one to get started.</p>
    </div>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="receptionist-list">
      {list.map((r) => (
        <motion.div key={r.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-md transition-shadow" data-testid={`receptionist-card-${r.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 grid place-items-center"><Bot className="w-5 h-5 text-blue-600" /></div>
            <span className={`text-xs px-2 py-0.5 rounded-full ${r.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.active ? "Active" : "Off"}</span>
          </div>
          <div className="font-semibold text-slate-900">{r.name}</div>
          <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Building2 className="w-3.5 h-3.5" /> {r.business_name}</div>
          {isAdmin && r.owner_id && <div className="text-[11px] text-slate-400 mt-1">owner: {r.owner_id.slice(0, 8)}…</div>}
          <div className="flex gap-2 mt-4">
            <button onClick={() => onOpen(r)} data-testid={`open-${r.id}`} className="flex-1 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:border-blue-500 hover:text-blue-600 inline-flex items-center justify-center gap-1.5"><Settings className="w-3.5 h-3.5" /> Manage</button>
            <button onClick={() => onTest(r)} data-testid={`test-${r.id}`} className="flex-1 py-2 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 inline-flex items-center justify-center gap-1.5"><PlayCircle className="w-3.5 h-3.5" /> Test</button>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function EditorView({ initial, isNew, tab, setTab, onBack, afterSave, afterDelete }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [summary, setSummary] = useState(null);
  const [calls, setCalls] = useState([]);
  const [messages, setMessages] = useState([]);
  const rid = form.id;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!rid) return;
    receptionistSummary(rid).then(setSummary).catch(() => {});
  }, [rid, tab]);

  useEffect(() => {
    if (!rid) return;
    if (tab === "calls") receptionistCalls(rid).then((d) => setCalls(d.calls || [])).catch(() => {});
    if (tab === "messages") receptionistMessages(rid).then((d) => setMessages(d.messages || [])).catch(() => {});
  }, [rid, tab]);

  const save = async () => {
    setErr("");
    if (!form.business_name.trim()) { setErr("Business name is required."); return; }
    setSaving(true);
    const payload = {
      name: form.name, business_name: form.business_name, greeting: form.greeting,
      kb_facts: form.kb_facts, faqs: (form.faqs || []).filter((f) => f.q?.trim()),
      hours_note: form.hours_note, transfer_number: form.transfer_number, phone_number: form.phone_number,
      notify_emails: form.notify_emails, voice: form.voice, languages: form.languages, active: form.active,
      hours_enabled: form.hours_enabled, timezone: form.timezone, open_days: form.open_days,
      open_time: form.open_time, close_time: form.close_time, after_hours_message: form.after_hours_message,
      slot_minutes: Number(form.slot_minutes) || 30, digest_enabled: form.digest_enabled,
      widget_color: form.widget_color, widget_avatar: form.widget_avatar, widget_welcome: form.widget_welcome,
    };
    try {
      const saved = isNew ? await receptionistCreate(payload) : await receptionistUpdate(rid, payload);
      setForm(saved);
      afterSave(saved);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!rid || !window.confirm("Delete this receptionist? This cannot be undone.")) return;
    try { await receptionistDelete(rid); afterDelete(); } catch { /* ignore */ }
  };

  const field = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const label = "block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1";
  const tabs = isNew ? [["setup", "Setup", Settings]] : [["setup", "Setup", Settings], ["test", "Test", PlayCircle], ["analytics", "Analytics", BarChart3], ["bookings", "Bookings", CalendarDays], ["calls", "Calls", MessageSquare], ["messages", "Messages", Inbox]];

  return (
    <div data-testid="receptionist-editor">
      <button onClick={onBack} data-testid="back-btn" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-4"><ArrowLeft className="w-4 h-4" /> Back to list</button>

      {!isNew && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="receptionist-summary">
          {[["Conversations", summary.calls, Phone], ["Messages", summary.messages, MessageSquare], ["Bookings", summary.appointments, CalendarDays], ["Leads", summary.leads, Users]].map(([lbl, val, Icon]) => (
            <div key={lbl} className="rounded-xl border border-slate-200 bg-white p-4">
              <Icon className="w-4 h-4 text-blue-600 mb-2" />
              <div className="text-2xl font-bold text-slate-900">{val}</div>
              <div className="text-xs text-slate-500">{lbl}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-5 border-b border-slate-200">
        {tabs.map(([key, lbl, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tab-${key}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === key ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            <Icon className="w-4 h-4" /> {lbl}
          </button>
        ))}
      </div>

      {tab === "setup" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4 rounded-2xl border border-slate-200 bg-white p-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={label}>Assistant name</label><input className={field} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="field-name" /></div>
              <div><label className={label}>Business name</label><input className={field} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="field-business" placeholder="e.g. Marino Trattoria" /></div>
            </div>
            <div><label className={label}>Greeting</label><input className={field} value={form.greeting} onChange={(e) => set("greeting", e.target.value)} data-testid="field-greeting" placeholder="How callers are welcomed" /></div>
            <div><label className={label}>Business knowledge (what the AI is allowed to say)</label><textarea rows={6} className={field} value={form.kb_facts} onChange={(e) => set("kb_facts", e.target.value)} data-testid="field-kb" placeholder="Hours, services, prices, address, booking policy…" /></div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={label}>FAQs</label>
                <button onClick={() => set("faqs", [...(form.faqs || []), { q: "", a: "" }])} data-testid="add-faq" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add FAQ</button>
              </div>
              <div className="space-y-2">
                {(form.faqs || []).map((f, i) => (
                  <div key={i} className="flex gap-2 items-start" data-testid={`faq-row-${i}`}>
                    <input className={field} placeholder="Question" value={f.q} onChange={(e) => { const n = [...form.faqs]; n[i] = { ...n[i], q: e.target.value }; set("faqs", n); }} data-testid={`faq-q-${i}`} />
                    <input className={field} placeholder="Answer" value={f.a} onChange={(e) => { const n = [...form.faqs]; n[i] = { ...n[i], a: e.target.value }; set("faqs", n); }} data-testid={`faq-a-${i}`} />
                    <button onClick={() => set("faqs", form.faqs.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 h-fit">
            <div className="flex items-center justify-between">
              <label className={label + " !mb-0"}>Active</label>
              <button onClick={() => set("active", !form.active)} data-testid="field-active" className={`relative w-11 h-6 rounded-full transition-colors ${form.active ? "bg-emerald-500" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.active ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
            <div><label className={label}>Business hours (note)</label><input className={field} value={form.hours_note} onChange={(e) => set("hours_note", e.target.value)} data-testid="field-hours" placeholder="Mon–Fri 9am–5pm" /></div>
            <div><label className={label}>Voice</label>
              <select className={field} value={form.voice} onChange={(e) => set("voice", e.target.value)} data-testid="field-voice">
                {VOICES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div><label className={label}>Notify emails (comma-separated)</label>
              <input className={field} value={(form.notify_emails || []).join(", ")} onChange={(e) => set("notify_emails", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid="field-notify" placeholder="you@business.com" />
            </div>
            <div><label className={label}>Transfer number (warm transfer)</label><input className={field} value={form.transfer_number} onChange={(e) => set("transfer_number", e.target.value)} data-testid="field-transfer" placeholder="+1…" /></div>
            <div><label className={label}>Phone number (Twilio, E.164)</label><input className={field} value={form.phone_number} onChange={(e) => set("phone_number", e.target.value)} data-testid="field-phone" placeholder="+15551234567" /></div>

            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <label className={label + " !mb-0"}>Business hours routing</label>
                <button onClick={() => set("hours_enabled", !form.hours_enabled)} data-testid="field-hours-enabled" className={`relative w-11 h-6 rounded-full transition-colors ${form.hours_enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.hours_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {form.hours_enabled && (
                <div className="space-y-2" data-testid="hours-block">
                  <div className="flex flex-wrap gap-1.5">
                    {DAYS.map((d, i) => (
                      <button key={d} onClick={() => set("open_days", form.open_days.includes(i) ? form.open_days.filter((x) => x !== i) : [...form.open_days, i])}
                        data-testid={`day-${i}`} className={`text-xs px-2.5 py-1 rounded-full border ${form.open_days.includes(i) ? "bg-blue-600 text-white border-blue-600" : "border-slate-300 text-slate-500"}`}>{d}</button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1"><label className={label}>Open</label><input type="time" className={field} value={form.open_time} onChange={(e) => set("open_time", e.target.value)} data-testid="field-open-time" /></div>
                    <div className="flex-1"><label className={label}>Close</label><input type="time" className={field} value={form.close_time} onChange={(e) => set("close_time", e.target.value)} data-testid="field-close-time" /></div>
                  </div>
                  <div><label className={label}>Timezone (IANA)</label><input className={field} value={form.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="field-timezone" placeholder="America/New_York" /></div>
                  <div><label className={label}>After-hours message</label><textarea rows={2} className={field} value={form.after_hours_message} onChange={(e) => set("after_hours_message", e.target.value)} data-testid="field-afterhours" placeholder="Thanks for calling — we're currently closed. I can take a message." /></div>
                </div>
              )}
            </div>

            <TelephonyStatus />

            <div className="pt-2 border-t border-slate-100 space-y-3">
              <div>
                <label className={label}>Appointment length (minutes)</label>
                <input type="number" min="5" max="240" step="5" className={field} value={form.slot_minutes} onChange={(e) => set("slot_minutes", e.target.value)} data-testid="field-slot" />
                <p className="text-[11px] text-slate-400 mt-1">Used for calendar invites; the same time slot can only be booked once.</p>
              </div>
              <div className="flex items-center justify-between">
                <label className={label + " !mb-0"}>Daily digest email</label>
                <button onClick={() => set("digest_enabled", !form.digest_enabled)} data-testid="field-digest" className={`relative w-11 h-6 rounded-full transition-colors ${form.digest_enabled ? "bg-emerald-500" : "bg-slate-300"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.digest_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
              {!isNew && <DigestTest rid={rid} />}
            </div>

            <div className="pt-2 border-t border-slate-100 space-y-3">
              <label className={label + " !mb-0"}>Widget branding</label>
              <div className="flex gap-2">
                <div className="flex-1"><label className={label}>Accent colour</label>
                  <input type="color" className="w-full h-9 rounded-lg border border-slate-300 cursor-pointer" value={form.widget_color || "#2563EB"} onChange={(e) => set("widget_color", e.target.value)} data-testid="field-widget-color" /></div>
                <div className="w-24"><label className={label}>Avatar</label>
                  <input className={field} maxLength={8} value={form.widget_avatar || ""} onChange={(e) => set("widget_avatar", e.target.value)} data-testid="field-widget-avatar" placeholder="💬" /></div>
              </div>
              <div><label className={label}>Welcome message</label>
                <textarea rows={2} className={field} value={form.widget_welcome || ""} onChange={(e) => set("widget_welcome", e.target.value)} data-testid="field-widget-welcome" placeholder="First thing visitors see (falls back to the greeting)" /></div>
              <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3" data-testid="widget-preview">
                <span className="w-11 h-11 rounded-full grid place-items-center text-xl text-white shadow" style={{ background: form.widget_color || "#2563EB" }}>{form.widget_avatar || "💬"}</span>
                <span className="text-xs text-slate-500">{form.widget_welcome || form.greeting || "How can I help you today?"}</span>
              </div>
            </div>

            {!isNew && form.active && <WidgetSnippet rid={rid} />}

            {err && <div className="text-xs text-red-600" data-testid="editor-error">{err}</div>}
            <div className="flex gap-2 pt-1">
              <button onClick={save} disabled={saving} data-testid="save-btn" className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isNew ? "Create" : "Save"}</button>
              {!isNew && <button onClick={del} data-testid="delete-btn" className="p-2.5 rounded-lg border border-slate-300 text-slate-500 hover:text-red-600 hover:border-red-300"><Trash2 className="w-4 h-4" /></button>}
            </div>
          </div>
        </div>
      )}

      {tab === "test" && rid && (
        <div className="max-w-xl">
          <p className="text-sm text-slate-500 mb-3">Talk to your receptionist just like a caller would — by text or voice. Everything you do here creates real records under Calls & Messages.</p>
          <ReceptionistTestConsole receptionistId={rid} />
        </div>
      )}

      {tab === "analytics" && rid && <AnalyticsTab rid={rid} />}
      {tab === "bookings" && rid && <BookingsTab rid={rid} />}

      {tab === "calls" && (
        <div className="space-y-3" data-testid="calls-list">
          {calls.length === 0 ? <Empty text="No conversations yet. Test your receptionist to see them here." /> :
            calls.map((c) => <CallCard key={c.id} rid={rid} call={c} />)}
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-3" data-testid="messages-list">
          {messages.length === 0 ? <Empty text="No messages taken yet." /> :
            messages.map((m) => (
              <div key={m.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`msg-${m.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-slate-900">{m.caller_name}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${m.urgency === "high" ? "bg-red-50 text-red-600" : m.urgency === "low" ? "bg-slate-100 text-slate-500" : "bg-amber-50 text-amber-700"}`}>{m.urgency}</span>
                </div>
                <div className="text-sm text-slate-600">{m.reason}</div>
                <div className="text-xs text-slate-400 mt-1.5">Callback: {m.callback} · {new Date(m.created_at).toLocaleString()} {m.notified && <span className="text-emerald-600">· emailed</span>}</div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function Empty({ text }) {
  return <div className="py-14 text-center rounded-2xl border border-dashed border-slate-300 bg-white text-slate-500 text-sm">{text}</div>;
}

function AnalyticsTab({ rid }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(14);
  useEffect(() => { setData(null); receptionistAnalytics(rid, days).then(setData).catch(() => setData(null)); }, [rid, days]);
  if (!data) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const busiest = [...data.hours].filter((h) => h.count > 0).sort((a, b) => b.count - a.count)[0];
  return (
    <div className="space-y-5" data-testid="analytics-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-3 text-sm">
          {[["calls", "Conversations"], ["bookings", "Bookings"], ["messages", "Messages"]].map(([k, l]) => (
            <div key={k} className="rounded-xl border border-slate-200 bg-white px-4 py-2.5">
              <div className="text-xl font-bold text-slate-900">{data.totals[k]}</div><div className="text-xs text-slate-500">{l}</div>
            </div>
          ))}
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="analytics-range" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value={7}>Last 7 days</option><option value={14}>Last 14 days</option><option value={30}>Last 30 days</option>
        </select>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><BarChart3 className="w-4 h-4 text-blue-600" /> Activity over time</div>
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={data.daily} margin={{ left: -18, right: 8, top: 6 }}>
            <defs>
              <linearGradient id="gCalls" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
              <linearGradient id="gBook" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
            <Tooltip />
            <Area type="monotone" dataKey="calls" name="Conversations" stroke="#2563EB" fill="url(#gCalls)" strokeWidth={2} />
            <Area type="monotone" dataKey="bookings" name="Bookings" stroke="#10b981" fill="url(#gBook)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Clock className="w-4 h-4 text-blue-600" /> Busiest hours {busiest && <span className="text-xs font-normal text-slate-400">· peak around {busiest.label}</span>}</div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={data.hours} margin={{ left: -18, right: 8, top: 6 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
            <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#94a3b8" }} interval={2} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
            <Tooltip />
            <Bar dataKey="count" name="Conversations" fill="#2563EB" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function BookingsTab({ rid }) {
  const [appts, setAppts] = useState(null);
  const [reminding, setReminding] = useState(null);
  const load = () => receptionistAppointments(rid).then((d) => setAppts(d.appointments || [])).catch(() => setAppts([]));
  useEffect(() => { load(); }, [rid]); // eslint-disable-line react-hooks/exhaustive-deps
  const remind = async (aid) => {
    setReminding(aid);
    try { const r = await receptionistRemind(rid, aid); setAppts((a) => a.map((x) => x.id === aid ? { ...x, reminder_sent: r.ok } : x)); }
    catch { /* ignore */ } finally { setReminding(null); }
  };
  if (appts === null) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (appts.length === 0) return <Empty text="No bookings yet. Bookings from the widget, phone or test console appear here." />;
  return (
    <div className="space-y-3" data-testid="bookings-list">
      {appts.map((a) => (
        <div key={a.id} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between gap-3" data-testid={`booking-${a.id}`}>
          <div>
            <div className="font-semibold text-slate-900">{a.name} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${a.status === "cancelled" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"}`}>{a.status}</span></div>
            <div className="text-sm text-slate-600">{a.service || "Appointment"} — {a.date} at {a.time}</div>
            <div className="text-xs text-slate-400 mt-0.5">{a.email || a.phone || "—"}{a.reminder_sent && <span className="text-emerald-600 ml-2">· reminded</span>}</div>
          </div>
          {a.status !== "cancelled" && a.email && (
            <button onClick={() => remind(a.id)} disabled={reminding === a.id} data-testid={`remind-${a.id}`} className="shrink-0 text-xs inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 disabled:opacity-50">
              {reminding === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Send reminder
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function TelephonyStatus() {
  const [st, setSt] = useState(null);
  useEffect(() => { telephonyStatus().then(setSt).catch(() => setSt(null)); }, []);
  if (!st) return null;
  return (
    <div className={`rounded-lg p-3 text-xs ${st.configured ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500 border border-slate-200"}`} data-testid="telephony-status">
      <div className="font-semibold mb-0.5 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Inbound phone calls {st.configured ? "· Live" : "· Not connected"}</div>
      {st.configured
        ? <span>Point your Twilio number's webhook to <code className="break-all">{st.webhook_url}</code> and set this receptionist's phone number above.</span>
        : <span>Add your Twilio credentials to go live. The webhook + warm-transfer engine are ready.</span>}
    </div>
  );
}

function DigestTest({ rid }) {
  const [state, setState] = useState("idle"); // idle | sending | sent
  const send = async () => {
    setState("sending");
    try { await receptionistDigestTest(rid); setState("sent"); setTimeout(() => setState("idle"), 3000); }
    catch { setState("idle"); }
  };
  return (
    <button onClick={send} disabled={state === "sending"} data-testid="digest-test-btn" className="text-xs inline-flex items-center gap-1.5 text-blue-600 hover:underline disabled:opacity-50">
      {state === "sending" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
      {state === "sent" ? "Sent!" : "Send a test digest now"}
    </button>
  );
}

function WidgetSnippet({ rid }) {
  const [copied, setCopied] = useState(false);
  const snippet = `<script src="${BACKEND}/api/receptionist/${rid}/widget.js" async></script>`;
  const copy = () => { navigator.clipboard?.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="widget-snippet">
      <div className="font-semibold text-xs text-slate-700 mb-1.5 flex items-center gap-1.5"><Code2 className="w-3.5 h-3.5" /> Add Ava to your website</div>
      <p className="text-[11px] text-slate-500 mb-2">Paste this one line before <code>&lt;/body&gt;</code>. A chat + booking bubble appears on your site, 24/7.</p>
      <code className="block text-[10px] leading-relaxed bg-slate-900 text-emerald-300 rounded-lg p-2.5 break-all" data-testid="widget-code">{snippet}</code>
      <button onClick={copy} data-testid="copy-snippet-btn" className="mt-2 text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy snippet"}
      </button>
    </div>
  );
}

function CallCard({ rid, call }) {
  const [c, setC] = useState(call);
  const [busy, setBusy] = useState(false);
  const summarize = async () => {
    setBusy(true);
    try { const d = await receptionistSummarizeCall(rid, c.id); setC({ ...c, summary: d.summary, outcome: d.outcome }); }
    catch { /* ignore */ } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`call-${c.id}`}>
      <div className="flex items-center justify-between text-xs text-slate-400 mb-2">
        <span className="inline-flex items-center gap-1.5">
          <Phone className="w-3.5 h-3.5" /> {c.channel || "web"} · {(c.turns || []).length} turns
          {c.outcome && <span className={`px-2 py-0.5 rounded-full ${OUTCOME_STYLES[c.outcome] || OUTCOME_STYLES.other}`} data-testid={`outcome-${c.id}`}>{c.outcome}</span>}
        </span>
        <span>{new Date(c.updated_at || c.created_at).toLocaleString()}</span>
      </div>
      {c.summary && <div className="mb-2 text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2" data-testid={`summary-${c.id}`}>{c.summary}</div>}
      <div className="space-y-1.5 max-h-40 overflow-y-auto">
        {(c.turns || []).slice(-8).map((t, i) => (
          <div key={i} className="text-sm"><span className={`font-semibold ${t.role === "user" ? "text-slate-900" : "text-blue-600"}`}>{t.role === "user" ? "Caller" : "Ava"}:</span> <span className="text-slate-600">{t.content}</span></div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button onClick={summarize} disabled={busy} data-testid={`summarize-${c.id}`} className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:border-blue-500 hover:text-blue-600 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {c.summary ? "Re-summarize" : "Summarize"}
        </button>
        {c.lead_id && <span className="text-xs text-emerald-600 font-medium">Lead captured</span>}
      </div>
    </div>
  );
}
